import React, { useRef, useState, useMemo } from "react"
import { Col, Row, Input, Button, Table, Tooltip, Typography, Tag, Space } from "antd"
import { SearchOutlined, UserOutlined } from "@ant-design/icons"
import Highlighter from "react-highlight-words"
import PageHeader from "../../../components/Elements/PageHeader"
import { useCollection } from "../../../hooks/useCollection"
import HandleOrderCredit from "./Helper/HandleOrderCredit"
import ManufacturerSelect from "./Helper/ManufacturerSelect"
import { BsFiletypeXls } from "react-icons/bs"

import * as XLSX from "xlsx"

const { Text } = Typography

// Create a formatter for Euro currency
const euroFormatter = new Intl.NumberFormat("en-GB", {
  style: "currency",
  currency: "EUR",
})

export default function AccountingClients() {
  const { loading, data } = useCollection("clients", ["user", "!=", "E1XNaWvT6PccXRSKB3JNlNehI362"])
  const { loading: manufacturerLoading, data: manufacturerList } = useCollection("manufacturers")

  //removes reinis
  const filteredData = data?.filter((client) => client.user !== "a8VctPNqlDPDPCd1k7BhXczYobt2")

  const [searchText, setSearchText] = useState("")
  const [searchedColumn, setSearchedColumn] = useState("")
  const searchInput = useRef(null)

  // Text search for Client and VAT columns
  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm()
    setSearchText(selectedKeys[0])
    setSearchedColumn(dataIndex)
  }

  const handleReset = (clearFilters) => {
    clearFilters()
    setSearchText("")
  }

  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
      <div style={{ padding: 8 }}>
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{ marginBottom: 8, display: "block" }}
        />
        <Button
          type="primary"
          onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
          icon={<SearchOutlined />}
          size="small"
          style={{ width: 90, marginRight: 8 }}>
          Search
        </Button>
        <Button onClick={() => handleReset(clearFilters)} size="small" style={{ width: 90 }}>
          Reset
        </Button>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />
    ),
    onFilter: (value, record) =>
      record[dataIndex]
        ? record[dataIndex].toString().toLowerCase().includes(value.toLowerCase())
        : "",
    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100)
      }
    },
    render: (text) =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text ? text.toString() : ""}
        />
      ) : (
        text
      ),
  })

  // Build filters for Manager column using unique userEmail values
  const managerFilters = useMemo(() => {
    if (!data) return []
    const uniqueManagers = [...new Set(data.map((item) => item.userEmail))]
    return uniqueManagers.map((email) => ({ text: email, value: email }))
  }, [data])

  // Build filters for Manufacturer column using manufacturerList
  const manufacturerFilters = useMemo(() => {
    if (!manufacturerList) return []
    return manufacturerList.map((manufacturer) => ({
      text: manufacturer.company,
      value: manufacturer.id,
    }))
  }, [manufacturerList])

  const exportToExcel = () => {
    if (!data || data.length === 0) {
      return
    }
    // Create a new array with only the fields you want to export
    const exportData = data.map((row) => ({
      Client: row.client,
      VAT: row.vat,
      "Contact Name": row.contactName,
      "Order Credit": row.orderCredit,
      Manufacturer: row.manufacturer ? row.manufacturer.company : "",
      Manager: row.userEmail,
    }))

    // Convert the filtered data to a worksheet
    const worksheet = XLSX.utils.json_to_sheet(exportData)
    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, worksheet, "Clients")
    XLSX.writeFile(workbook, "clients.xlsx")
  }

  const columns = [
    {
      title: "Client",
      dataIndex: "client",
      key: "client",
      ...getColumnSearchProps("client"),
    },
    {
      title: "VAT",
      dataIndex: "vat",
      ellipsis: { showTitle: false },
      render: (vat) => (
        <Tooltip placement="topLeft" title={vat}>
          {vat}
        </Tooltip>
      ),
      sorter: (a, b) => a.vat.localeCompare(b.vat),
      ...getColumnSearchProps("vat"),
    },
    {
      title: "Contact Name",
      dataIndex: "contactName",
    },
    {
      title: "Credit used",
      dataIndex: "orderCredit",
      align: "center",
      render: (value, record) => {
        const creditUsed = Math.round((record.creditUsed || 0) * 100) / 100
        const creditLimit = Math.round((record.creditLimit || 0) * 100) / 100
        return (
          <Text italic>
            {euroFormatter.format(creditUsed)} / {euroFormatter.format(creditLimit)}
          </Text>
        )
      },
    },
    {
      title: "Maximum Credit",
      dataIndex: "orderCredit",
      align: "center",
      sorter: (a, b) => a.orderCredit - b.orderCredit,
      render: (value, client) => <HandleOrderCredit client={client} />,
    },
    {
      title: "Manufacturer",
      align: "center",
      filters: manufacturerFilters,
      onFilter: (value, record) => record.manufacturer && record.manufacturer.id === value,
      render: (value, record) => (
        <ManufacturerSelect
          client={record}
          loading={manufacturerLoading}
          manufacturerList={manufacturerList}
        />
      ),
    },
    {
      title: "Manager",
      dataIndex: "userEmail",
      key: "userEmail",
      filters: managerFilters,
      onFilter: (value, record) => record.userEmail === value,
      render: (value) => <Tag color="blue">{value}</Tag>,
    },
  ]

  return (
    <div style={{ padding: 15 }}>
      <Row gutter={[15, 15]}>
        <Col span={24}>
          <PageHeader text="Accounting Clients" icon={<UserOutlined />} />
        </Col>
        <Col span={24}>
          <Button icon={<BsFiletypeXls />} type="primary" onClick={exportToExcel}>
            Export to Excel
          </Button>
        </Col>
        <Col span={24}>
          <Table
            size="small"
            columns={columns}
            dataSource={filteredData}
            loading={loading}
            rowKey={(record) => record.id}
          />
        </Col>
      </Row>
    </div>
  )
}
